import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import {
  makeStyles,
  shorthands,
  Button,
  Input,
  Spinner,
  Text,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  TableCellLayout,
  MessageBar,
  MessageBarBody,
  Card,
} from "@fluentui/react-components";
import {
  SearchRegular,
  DocumentRegular,
  DismissCircleRegular,
  ChevronRightRegular,
} from "@fluentui/react-icons";
import { Tender } from "../types/tender";
import { tenderService } from "../services/tenderService";

// Styles following App.tsx patterns
const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    ...shorthands.padding("16px"),
    ...shorthands.gap("16px"),
  },
  header: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("12px"),
  },
  title: {
    fontSize: "16px",
    fontWeight: "800",
    fontFamily: "'JetBrains Mono', monospace",
    color: "#0B5D5D",
  },
  subtitle: {
    fontSize: "13px",
    color: "#666",
  },
  searchContainer: {
    display: "flex",
    ...shorthands.gap("8px"),
    width: "100%",
  },
  searchInput: {
    flexGrow: 1,
  },
  tableCard: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
    ...shorthands.borderRadius("6px"),
    boxShadow: "0 2px 8px rgba(11, 93, 93, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06)",
  },
  tableContainer: {
    overflowY: "auto",
    maxHeight: "400px",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    ...shorthands.padding("40px", "16px"),
    ...shorthands.gap("16px"),
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    ...shorthands.padding("60px", "20px"),
    ...shorthands.gap("12px"),
    textAlign: "center",
  },
  emptyIcon: {
    fontSize: "48px",
    color: "#999",
  },
  selectButton: {
    backgroundColor: "#0B5D5D",
    color: "#ffffff",
    ":hover": {
      backgroundColor: "#094a4a",
    },
    ":active": {
      backgroundColor: "#063030",
    },
  },
  tenderRow: {
    cursor: "pointer",
    ":hover": {
      backgroundColor: "#F8F8F8",
    },
    transitionDuration: "0.2s",
  },
  tenderCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    ...shorthands.padding("12px", "16px"),
  },
  tenderTitleContainer: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap("12px"),
    flexGrow: 1,
    minWidth: 0, // Allow text truncation
  },
  tenderTitle: {
    fontWeight: "500",
    color: "#1A1A1A",
    fontSize: "14px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  chevronIcon: {
    fontSize: "18px",
    color: "#0B5D5D",
    flexShrink: 0,
  },
});

interface TenderSelectionPageProps {
  companyName: string;
  onTenderSelected: (tender: Tender) => void;
}

export const TenderSelectionPage: React.FC<TenderSelectionPageProps> = ({
  companyName,
  onTenderSelected,
}) => {
  const styles = useStyles();
  const [tenders, setTenders] = useState<Tender[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search input (500ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch tenders when component mounts or search changes
  const fetchTenders = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      console.log(`[TENDER_SELECTION] Fetching tenders for company: ${companyName}`);
      const result = await tenderService.getTendersForCompany(
        companyName,
        debouncedSearch || undefined,
        50
      );
      setTenders(result.data.contracts);
      console.log(`[TENDER_SELECTION] Loaded ${result.data.contracts.length} tenders`);
    } catch (err) {
      console.error(`[TENDER_SELECTION] Error fetching tenders:`, err);
      setError(err instanceof Error ? err.message : "Failed to load tenders");
    } finally {
      setIsLoading(false);
    }
  }, [companyName, debouncedSearch]);

  useEffect(() => {
    fetchTenders();
  }, [fetchTenders]);

  const handleSelectTender = (tender: Tender) => {
    console.log(`[TENDER_SELECTION] Tender selected:`, tender);
    onTenderSelected(tender);
  };

  const formatDate = (isoDate: string): string => {
    try {
      return new Date(isoDate).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return isoDate;
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Text className={styles.title}>Select a Tender/Contract</Text>
        <Text className={styles.subtitle}>
          Choose the tender/contract related to this email
        </Text>

        <div className={styles.searchContainer}>
          <Input
            className={styles.searchInput}
            placeholder="Search by title..."
            value={searchQuery}
            onChange={(e, data) => setSearchQuery(data.value)}
            contentBefore={<SearchRegular />}
          />
        </div>
      </div>

      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {isLoading ? (
        <div className={styles.loadingContainer}>
          <Spinner size="large" />
          <Text>Loading tenders...</Text>
        </div>
      ) : tenders.length === 0 ? (
        <div className={styles.emptyState}>
          <DismissCircleRegular className={styles.emptyIcon} />
          <Text weight="semibold">No tenders found</Text>
          <Text className={styles.subtitle}>
            {searchQuery
              ? `No results for "${searchQuery}". Try a different search term.`
              : "No tenders available for your company. Contact support if this is unexpected."}
          </Text>
          {searchQuery && (
            <Button
              appearance="secondary"
              onClick={() => setSearchQuery("")}
            >
              Clear Search
            </Button>
          )}
        </div>
      ) : (
        <Card className={styles.tableCard}>
          <div className={styles.tableContainer}>
            <Table size="small">
              <TableBody>
                {tenders.map((tender) => (
                  <TableRow
                    key={tender.id}
                    className={styles.tenderRow}
                    onClick={() => handleSelectTender(tender)}
                  >
                    <TableCell>
                      <div className={styles.tenderCell}>
                        <div className={styles.tenderTitleContainer}>
                          <DocumentRegular style={{ fontSize: "16px", color: "#0B5D5D", flexShrink: 0 }} />
                          <div className={styles.tenderTitle} title={tender.title}>
                            {tender.title}
                          </div>
                        </div>
                        <ChevronRightRegular className={styles.chevronIcon} />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default TenderSelectionPage;
